import React from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeftIcon, ChevronRightIcon } from '../../ui/Icons';
import { FlyoutRailBackdrop, FlyoutRailPanel } from '../../ui/FlyoutFromRail';
import { useIsMobile } from '../../../hooks/useBreakpoint';
import { menuLabel } from '../../../i18n/labels';
import { cn } from '../../../lib/utils';
import type { MenuItem } from '../Sidebar';
import {
  SIDEBAR_FLYOUT_WIDTH_PX,
  sidebarFlyoutBodyClass,
  sidebarFlyoutHeaderClass,
  sidebarFlyoutItemClass,
  sidebarFlyoutPanelClass,
  sidebarIconWellClass,
} from './sidebarDesign';

const getViewPreference = (moduleName: string, preferences: Record<string, string> | null): string | null => {
  if (!preferences) return null;
  return preferences[moduleName] || null;
};

export interface AppSidebarFlyoutProps {
  isOpen: boolean;
  onClose: () => void;
  item: MenuItem;
  leftPosition: number;
  onItemClick?: (item: MenuItem, hasSubitems: boolean) => void;
  onBack?: () => void;
  showBack?: boolean;
  viewPreferences?: Record<string, string>;
}

export const AppSidebarFlyout: React.FC<AppSidebarFlyoutProps> = ({
  isOpen,
  onClose,
  item,
  leftPosition,
  onItemClick,
  onBack,
  showBack = false,
  viewPreferences = {},
}) => {
  const { t } = useTranslation('sidebar');
  const itemLabel = menuLabel(t, item.name);
  const isMobile = useIsMobile();
  const location = useLocation();
  const navigate = useNavigate();

  if (!isOpen || !item.children || item.children.length === 0) {
    return null;
  }

  const isCurrentPath = (href?: string) => {
    if (!href) return false;
    return location.pathname === href || location.pathname.startsWith(`${href}/`);
  };

  const getPreferredViewPath = (menuItem: MenuItem): string | undefined => {
    if (!menuItem.views || menuItem.views.length === 0) return menuItem.path;
    const savedViewType = getViewPreference(menuItem.name, viewPreferences);
    if (savedViewType) {
      if (menuItem.name === 'Emails') return '/emails?view=card';
      const savedView = menuItem.views.find((view) => view.type === savedViewType);
      if (savedView) return savedView.path;
    }
    return menuItem.views[0].path;
  };

  const panelWidth = isMobile
    ? `min(${SIDEBAR_FLYOUT_WIDTH_PX}px, calc(100vw - ${leftPosition}px - 16px))`
    : `${SIDEBAR_FLYOUT_WIDTH_PX}px`;

  const tree = (
    <>
      <FlyoutRailBackdrop onClose={onClose} />
      <FlyoutRailPanel
        left={leftPosition}
        ariaLabel={itemLabel}
        variant="floating"
        className={sidebarFlyoutPanelClass()}
        panelStyle={{ width: panelWidth }}
      >
        <div className={sidebarFlyoutHeaderClass()}>
          <div className="flex min-w-0 flex-1 items-center gap-3">
            {showBack && onBack && (
              <button
                type="button"
                onClick={onBack}
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-slate-300 transition-colors hover:bg-white/10 hover:text-white"
                title={t('chrome.back')}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </button>
            )}
            {item.icon && (
              <span className={sidebarIconWellClass(true)}>{item.icon}</span>
            )}
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
                {t('chrome.menuHeader')}
              </p>
              <p className="truncate text-sm font-semibold text-white">{itemLabel}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-lg leading-none text-slate-400 transition-colors hover:bg-white/10 hover:text-white"
            aria-label={t('chrome.closeMenu')}
          >
            ×
          </button>
        </div>

        <div className={sidebarFlyoutBodyClass()}>
          <div className="space-y-0.5">
            {item.children.map((subitem, index) => {
              const hasSubitems = Boolean(subitem.children && subitem.children.length > 0);
              const isActive = isCurrentPath(subitem.path);
              const label = menuLabel(t, subitem.name);

              if (hasSubitems) {
                return (
                  <button
                    key={subitem.path ?? `${label}-${index}`}
                    type="button"
                    onClick={() => onItemClick?.(subitem, true)}
                    className={sidebarFlyoutItemClass(false, true)}
                  >
                    <div className="flex min-w-0 items-center gap-3">
                      {subitem.icon ? (
                        <span className={sidebarIconWellClass(false)}>{subitem.icon}</span>
                      ) : null}
                      <span className="truncate font-medium">{label}</span>
                    </div>
                    <ChevronRightIcon className="h-4 w-4 shrink-0 opacity-60" />
                  </button>
                );
              }

              return (
                <button
                  key={subitem.path ?? `${label}-${index}`}
                  type="button"
                  onClick={() => {
                    const preferredPath = getPreferredViewPath(subitem);
                    if (preferredPath) {
                      navigate(preferredPath);
                      onClose();
                    }
                  }}
                  className={sidebarFlyoutItemClass(isActive)}
                >
                  {subitem.icon ? (
                    <span className={sidebarIconWellClass(isActive)}>{subitem.icon}</span>
                  ) : null}
                  <span className="flex-1 truncate font-medium">{label}</span>
                  {subitem.badge ? (
                    <span className="shrink-0 rounded-full bg-red-500 px-1.5 py-0.5 text-xs text-white">
                      {subitem.badge > 9 ? '9+' : subitem.badge}
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </div>
      </FlyoutRailPanel>
    </>
  );

  return typeof document !== 'undefined' ? createPortal(tree, document.body) : null;
};

export default AppSidebarFlyout;
